"use client";

import { PoiRadiusItem } from "@/lib/types/listing";
import { useNearViewport } from "@/lib/hook/use-near-viewport";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import IconPoi from "@/components/icon/IconPoi";
import { usePostHog } from "posthog-js/react";
import { motion, AnimatePresence, Variants } from "framer-motion";

type Props = {
  geohash7: string;
};

const distanceOptions = [500, 1000, 2000, 5000];

const metersToKilometers = (meters: number): number => {
  return meters / 1000;
};

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.25, ease: "easeOut" },
  },
};

const PoiRadiusItems: React.FC<Props> = ({ geohash7 }) => {
  const posthog = usePostHog();
  const t = useTranslations("poi");
  const [selectedOption, setSelectedOption] = useState(distanceOptions[1]);
  const [containerRef, isNearViewport] = useNearViewport<HTMLDivElement>();

  const { data, isLoading } = useQuery({
    queryKey: ["listing", "poi-radius", geohash7, selectedOption],
    enabled: isNearViewport,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("distance", selectedOption.toString());
      params.append("geohash7", geohash7);

      const response = await fetch(
        `/api/site/public/listing/poi-radius?${params.toString()}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        return [];
      }

      const data = (await response.json()) as {
        success: boolean;
        data: PoiRadiusItem[];
      };
      if (!data.success) {
        return [];
      }

      return data.data;
    },
  });

  const nf = new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumFractionDigits: 0,
  });
  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      <span className="text-accent-foreground font-semibold">
        {t.rich("radius-info.label", {
          select: () => (
            <DistanceSelect
              selected={selectedOption}
              onChange={(value) => {
                setSelectedOption(value);
                posthog.capture("poi_radius_distance_change", {
                  distance: value,
                });
              }}
            />
          ),
        })}
      </span>

      <AnimatePresence>
        {data?.length === 0 && !isLoading && (
          <motion.p
            key="no-data"
            className="text-muted-foreground italic text-center mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {t("radius-info.not-available")}
          </motion.p>
        )}
      </AnimatePresence>

      {((data && data.length > 0) || isLoading) && (
        <motion.div
          key={`poi-items-${selectedOption}-${isLoading}`}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
        >
          {isLoading ? (
            <>
              {Array.from({ length: 8 }).map((_, index) => (
                <motion.div
                  key={`poi-skeleton-${index}-${selectedOption}`}
                  variants={itemVariants}
                >
                  <Skeleton className="w-full h-11 rounded-md" />
                </motion.div>
              ))}
            </>
          ) : (
            <>
              {data?.map((poi) => (
                <motion.div
                  key={`${poi.type}-${selectedOption}`}
                  variants={itemVariants}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-card h-11 px-1.5 py-1.5 flex flex-row gap-2 items-center rounded-lg shadow-sm border border-border"
                >
                  <div className="w-8 h-8 min-w-8 rounded-md bg-secondary flex items-center justify-center">
                    <IconPoi type={poi.type} className="w-4 h-4 text-primary" />
                  </div>
                  <div className="w-full overflow-hidden">
                    <p className="text-base font-bold">
                      {nf.format(poi.count)}
                    </p>
                    <p className="uppercase text-xs -mt-1 line-clamp-1 text-muted-foreground">
                      {t(`type.${poi.type}`, {
                        count: poi.count,
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default PoiRadiusItems;

type DistanceSelectProps = {
  selected: number;
  onChange: (value: number) => void;
};

const DistanceSelect: React.FC<DistanceSelectProps> = ({
  selected,
  onChange,
}) => {
  const t = useTranslations("poi");
  return (
    <span className="inline-block align-middle">
      <Select
        value={selected.toString()}
        onValueChange={(value) => onChange(parseInt(value, 10))}
      >
        <SelectTrigger
          aria-label={t("radius-info.select-placeholder")}
          className="w-24 inline-flex h-8 font-bold"
        >
          <SelectValue placeholder={t("radius-info.select-placeholder")} />
        </SelectTrigger>
        <SelectContent className="w-32">
          <SelectGroup>
            <SelectLabel className="text-xs text-muted-foreground">
              {t("radius-info.select-placeholder")}
            </SelectLabel>
            {distanceOptions.map((option) => (
              <SelectItem
                key={option}
                value={option.toString()}
                className="font-semibold"
              >
                {metersToKilometers(option)} km
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </span>
  );
};
